<template>
  <div class="app-container">
    <div class="lesson-plan-container">
      <div class="lesson-plan-header">
        <h1>智能教案生成</h1>
        <p>填写课程信息，AI将为您生成完整的教案内容</p>
      </div>

      <el-card class="lesson-form-card">
        <el-form :model="lessonForm" label-position="top" :rules="rules" ref="lessonFormRef">
          <el-form-item label="年级" prop="grade">
            <el-select v-model="lessonForm.grade" placeholder="请选择年级">
              <el-option v-for="item in gradeOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="科目" prop="subject">
            <el-select v-model="lessonForm.subject" placeholder="请选择科目">
              <el-option v-for="item in subjectOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="教材出版社" prop="publisher">
            <el-select v-model="lessonForm.publisher" placeholder="请选择教材出版社">
              <el-option v-for="item in publisherOptions" :key="item.value" :label="item.label" :value="item.value"></el-option>
            </el-select>
          </el-form-item>

          <el-form-item label="课程内容" prop="content">
            <el-input type="textarea" v-model="lessonForm.content" :rows="5" placeholder="请输入课程内容或教学目标"></el-input>
          </el-form-item>

          <el-form-item>
            <el-button type="primary" @click="generateLessonPlan" :loading="loading" icon="el-icon-magic-stick">生成教案</el-button>
            <el-button @click="resetForm" plain icon="el-icon-refresh">重置</el-button>
          </el-form-item>
        </el-form>
      </el-card>

      <div v-if="aiResponse" class="lesson-plan-results">
        <h2>AI 教案内容</h2>
        <el-card class="result-card">
          <div class="ai-response">
            <div class="ai-avatar">
              <i class="el-icon-edit-outline"></i>
            </div>
            <div class="ai-content" v-html="formattedResponse"></div>
          </div>
          <div class="action-buttons">
            <el-button type="success" size="small" icon="el-icon-download" @click="downloadLessonPlan">下载教案</el-button>
            <el-button type="primary" size="small" icon="el-icon-copy-document" @click="copyLessonPlan">复制内容</el-button>
          </div>
        </el-card>
      </div>

      <div v-if="noResults && !loading" class="no-results">
        <el-card class="empty-card">
          <div class="empty-container">
            <i class="el-icon-warning-outline empty-icon"></i>
            <span class="empty-text">生成教案失败，请重试</span>
          </div>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import { textCompletion } from '@/api/ai'
import { marked } from 'marked'

export default {
  name: 'LessonPlan',
  data() {
    return {
      lessonForm: {
        grade: '',
        subject: '',
        publisher: '',
        content: ''
      },
      gradeOptions: [
        { value: '一年级', label: '一年级' },
        { value: '二年级', label: '二年级' },
        { value: '三年级', label: '三年级' },
        { value: '四年级', label: '四年级' },
        { value: '五年级', label: '五年级' },
        { value: '六年级', label: '六年级' }
      ],
      subjectOptions: [
        { value: '语文', label: '语文' },
        { value: '数学', label: '数学' },
        { value: '英语', label: '英语' },
        { value: '计算机', label: '计算机' }
      ],
      publisherOptions: [
        { value: '人教版', label: '人教版' },
        { value: '苏教版', label: '苏教版' },
        { value: '北师大版', label: '北师大版' },
        { value: '沪教版', label: '沪教版' },
        { value: '浙教版', label: '浙教版' }
      ],
      rules: {
        grade: [{ required: true, message: '请输入年级', trigger: 'blur' }],
        subject: [{ required: true, message: '请输入科目', trigger: 'blur' }],
        content: [{ required: true, message: '请输入课程内容', trigger: 'blur' }]
      },
      aiResponse: '',
      loading: false,
      noResults: false
    }
  },
  computed: {
    formattedResponse() {
      if (!this.aiResponse) return ''
      // 使用 marked 将 markdown 格式转换为 HTML
      try {
        // 增加katex渲染支持
        const renderer = new marked.Renderer()
        const originalCode = renderer.code

        renderer.code = function(code, language) {
          if (language === 'latex' || language === 'math' || language === 'tex') {
            return `<div class="math-formula">${code}</div>`
          }
          return originalCode.call(this, code, language)
        }

        // 配置marked选项
        marked.setOptions({
          renderer: renderer,
          breaks: true,
          gfm: true
        })

        // 预处理Markdown内容，特别处理数学公式部分
        let content = this.aiResponse

        // 1. 检测并标准化数学公式标记
        // 将$符号的公式转换为\( \)格式
        content = content
          .replace(/(?<!\$|\\\()(?<!\\\))\$(?!\$)([^$\n]+?)(?<!\$)\$(?!\$)(?!\\\))/g, '\\($1\\)')
          .replace(/\$\$([^$]+?)\$\$/g, '\\[$1\\]')

        // 2. 处理LaTeX公式的HTML包装
        content = content
          // 行内公式
          .replace(/\\\(/g, '<span class="inline-formula">\\(')
          .replace(/\\\)/g, '\\)</span>')
          // 行间公式
          .replace(/\\\[/g, '<div class="block-formula">\\[')
          .replace(/\\\]/g, '\\]</div>')

        // 应用Markdown转换
        const htmlContent = marked(content)

        // 在nextTick中渲染公式，而不是在computed中使用setTimeout
        this.$nextTick(this.scheduleFormulaRendering)

        return htmlContent
      } catch (e) {
        console.error('Markdown转换失败:', e)
        return this.aiResponse
      }
    }
  },
  methods: {
    // 安排公式渲染的方法
    scheduleFormulaRendering() {
      setTimeout(() => {
        this.renderMathFormulas()
      }, 100)
    },

    generateLessonPlan() {
      this.$refs.lessonFormRef.validate(valid => {
        if (!valid) {
          this.$message.warning('请完善表单信息')
          return
        }

        this.loading = true
        this.noResults = false
        this.aiResponse = ''

        // 构建提示词
        const prompt = `请为我生成一份详细的教案，具体信息如下：
年级：${this.lessonForm.grade}
科目：${this.lessonForm.subject}
${this.lessonForm.publisher ? '教材出版社：' + this.lessonForm.publisher : ''}
课程内容：${this.lessonForm.content}

请按照以下结构生成教案：
1. 教学目标
2. 教学重点与难点
3. 教学准备
4. 教学过程（包括导入、新课讲授、巩固练习、总结）
5. 板书设计
6. 课后反思

请尽可能详细地提供每个部分的内容，特别是教学过程部分。`

        // 调用后端 AI 文本接口
        textCompletion(prompt)
          .then(this.handleApiResponse)
          .catch(this.handleApiError)
          .finally(() => {
            this.loading = false
          })
      })
    },

    // 处理API响应的通用方法
    handleApiResponse(response) {
      console.log('AI响应数据:', response)

      // 根据后端代码，响应结构为Result类型，成功码为1
      if (response.code === 1) {
        // data字段包含AI的回答
        this.aiResponse = response.data
        this.noResults = !this.aiResponse

        // 如果成功获取到响应，加载KaTeX渲染数学公式
        if (this.aiResponse) {
          this.$nextTick(() => {
            this.renderMathFormulas()
          })
        }
      } else if (response.code === 200) {
        // 某些API可能使用200作为成功码
        this.aiResponse = response.data || response.result || response.message || ''
        this.noResults = !this.aiResponse

        if (this.aiResponse) {
          this.$nextTick(() => {
            this.renderMathFormulas()
          })
        }
      } else if (typeof response === 'string') {
        // 直接返回字符串的情况
        this.aiResponse = response
        this.noResults = false

        this.$nextTick(() => {
          this.renderMathFormulas()
        })
      } else {
        // 失败情况
        this.aiResponse = ''
        this.noResults = true
        this.$message.error(response.msg || '生成失败')
      }
    },

    // 处理API错误的通用方法
    handleApiError(error) {
      console.error('AI 教案生成失败:', error)
      this.aiResponse = ''
      this.noResults = true

      // 提取更友好的错误信息
      let errorMsg = '生成失败'

      if (error.response && error.response.data) {
        errorMsg += ': ' + (error.response.data.msg || error.message || '未知错误')
      } else {
        errorMsg += ': ' + (error.message || '未知错误')
      }

      this.$message.error(errorMsg)
    },

    // 渲染数学公式
    renderMathFormulas() {
      if (window.katex) {
        try {
          const formulas = document.querySelectorAll('.inline-formula, .block-formula, .math-formula')

          // 第一步：预处理公式内容，统一格式
          formulas.forEach(el => {
            try {
              let formula = el.textContent
                .replace(/^\\\(|\\\)$/g, '')
                .replace(/^\\\[|\\\]$/g, '')
                .trim()

              // 使用全局公式修复函数
              if (window.fixLatexNotation) {
                formula = window.fixLatexNotation(formula)
              }

              // 使用KaTeX渲染公式
              window.katex.render(formula, el, {
                throwOnError: false,
                displayMode: el.classList.contains('block-formula') || el.classList.contains('math-formula'),
                output: 'html',
                trust: true,
                strict: false
              })
            } catch (error) {
              console.error('KaTeX渲染失败:', error, '公式内容:', el.textContent)
              // 保留原始内容，添加错误样式
              el.classList.add('render-error')
              el.setAttribute('title', '公式渲染失败: ' + error.message)
            }
          })

          // 处理渲染后的位置对齐
          document.querySelectorAll('.inline-formula .katex').forEach(el => {
            el.style.verticalAlign = 'middle'
          })
        } catch (e) {
          console.error('公式渲染过程出错:', e)
        }
      } else {
        console.warn('KaTeX未加载，正在尝试加载...')
        // 尝试动态加载KaTeX
        this.loadKaTeX()
      }
    },

    // 添加动态加载KaTeX的方法
    loadKaTeX() {
      if (window.katex) return // 已加载则直接返回

      const loadScript = (src) => {
        return new Promise((resolve, reject) => {
          const script = document.createElement('script')
          script.src = src
          script.onload = resolve
          script.onerror = reject
          document.head.appendChild(script)
        })
      }

      const loadCSS = (href) => {
        return new Promise((resolve, reject) => {
          const link = document.createElement('link')
          link.rel = 'stylesheet'
          link.href = href
          link.onload = resolve
          link.onerror = reject
          document.head.appendChild(link)
        })
      }

      Promise.all([
        loadCSS('https://unpkg.com/katex@0.16.4/dist/katex.min.css'),
        loadScript('https://unpkg.com/katex@0.16.4/dist/katex.min.js')
      ]).then(() => {
        // 加载扩展
        return Promise.all([
          loadScript('https://unpkg.com/katex@0.16.4/dist/contrib/auto-render.min.js'),
          loadScript('https://unpkg.com/katex@0.16.4/dist/contrib/mhchem.min.js')
        ])
      }).then(() => {
        console.log('KaTeX加载成功')
        // 重新尝试渲染公式
        setTimeout(() => {
          this.renderMathFormulas()
        }, 100)
      }).catch(error => {
        console.error('KaTeX加载失败:', error)
      })
    },

    // 重置表单
    resetForm() {
      this.$refs.lessonFormRef.resetFields()
      this.aiResponse = ''
      this.noResults = false
    },

    // 下载教案
    downloadLessonPlan() {
      if (!this.aiResponse) return

      const fileName = `${this.lessonForm.grade}-${this.lessonForm.subject}-教案.md`
      const content = this.aiResponse

      const blob = new Blob([content], { type: 'text/markdown;charset=utf-8' })

      // 创建下载链接
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = fileName
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      this.$message.success('教案下载成功')
    },

    // 复制教案内容
    copyLessonPlan() {
      if (!this.aiResponse) return

      const textarea = document.createElement('textarea')
      textarea.value = this.aiResponse
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()

      try {
        document.execCommand('copy')
        this.$message.success('教案内容已复制到剪贴板')
      } catch (err) {
        this.$message.error('复制失败，请手动复制')
      } finally {
        document.body.removeChild(textarea)
      }
    }
  }
}
</script>

<style scoped>
.lesson-plan-container {
  max-width: 900px;
  margin: 0 auto;
  padding: 20px;
}

.lesson-plan-header {
  text-align: center;
  margin-bottom: 30px;
}

.lesson-plan-header h1 {
  font-size: 28px;
  color: #303133;
  margin-bottom: 10px;
}

.lesson-plan-header p {
  color: #606266;
  font-size: 16px;
}

.lesson-form-card {
  margin-bottom: 30px;
  border-radius: 8px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08);
}

.lesson-plan-results {
  margin-top: 30px;
}

.lesson-plan-results h2 {
  font-size: 20px;
  margin-bottom: 15px;
  color: #303133;
  padding-left: 10px;
  border-left: 4px solid #409EFF;
}

.result-card {
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08);
  overflow: hidden;
}

.ai-response {
  display: flex;
  padding: 20px;
}

.ai-avatar {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  margin-right: 20px;
  border-radius: 50%;
  overflow: hidden;
  background-color: #ecf5ff;
  display: flex;
  align-items: center;
  justify-content: center;
}

.ai-avatar i {
  font-size: 24px;
  color: #409EFF;
}

.ai-content {
  flex-grow: 1;
  line-height: 1.7;
  font-size: 15px;
  color: #333;
}

.ai-content p {
  margin-bottom: 15px;
}

.ai-content ul,
.ai-content ol {
  padding-left: 20px;
  margin-bottom: 15px;
}

.ai-content h1,
.ai-content h2,
.ai-content h3,
.ai-content h4 {
  margin-top: 20px;
  margin-bottom: 12px;
  color: #1a1a1a;
}

.action-buttons {
  display: flex;
  justify-content: flex-end;
  padding: 0 20px 20px;
  gap: 10px;
}

.empty-card {
  text-align: center;
  padding: 40px 0;
  border-radius: 8px;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.08);
}

.empty-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.empty-icon {
  font-size: 48px;
  color: #909399;
  margin-bottom: 15px;
}

.empty-text {
  color: #909399;
  font-size: 16px;
}

/* 数学公式样式 */
.inline-formula {
  display: inline-block;
  margin: 0 2px;
  vertical-align: middle;
}

.block-formula {
  display: block;
  margin: 15px 0;
  text-align: center;
  overflow-x: auto;
  padding: 10px 0;
}

.math-formula {
  display: block;
  margin: 15px 0;
  text-align: center;
  overflow-x: auto;
  padding: 15px 0;
  background-color: #f9f9f9;
  border-radius: 4px;
}

.render-error {
  color: #f56c6c;
  border: 1px dashed #f56c6c;
  padding: 5px;
  margin: 5px 0;
  display: inline-block;
}

/* 确保公式在移动设备上也能正常显示 */
@media screen and (max-width: 768px) {
  .block-formula, .math-formula {
    font-size: 90%;
    padding: 10px 5px;
    overflow-x: auto;
  }
}
</style>

<style>
/* 全局样式 */
.katex {
  font-size: 1.1em !important;
  line-height: 1.5em !important;
}

.katex-display {
  overflow-x: auto;
  overflow-y: hidden;
  padding: 8px 0;
  margin: 10px 0;
  text-align: center;
}

/* 改进公式容器样式 */
.math-formula, .block-formula {
  background-color: #f9f9f9;
  border-radius: 6px;
  padding: 10px;
  margin: 15px 0;
  overflow-x: auto;
}

.inline-formula {
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin: 0 3px;
}

/* 针对MacOS和iOS设备的优化 */
@media screen and (-webkit-min-device-pixel-ratio: 0) {
  .katex-display > .katex {
    min-width: min-content;
  }
}

/* 优化分数显示 */
.katex .mfrac .frac-line {
  border-bottom-width: 1px;
  margin: 0 -0.1em;
}

.katex .mfrac .frac-line::after {
  border-bottom-width: 1px;
}

/* 分子分母的间距 */
.katex .mfrac .msupsub .mfrac-numer {
  padding-bottom: 0.1em;
}

.katex .mfrac .msupsub .mfrac-denom {
  padding-top: 0.1em;
}

/* 优化上标下标显示 */
.katex .msupsub {
  text-align: left;
}

/* 改进大型操作符 (如求和、积分) 的显示 */
.katex .mop {
  margin-top: 0.1em;
  margin-bottom: 0.15em;
}

/* 优化矩阵显示 */
.katex .array {
  margin: 0 0.1em;
}

/* 提高公式渲染错误的可见性 */
.render-error {
  color: #f56c6c;
  border: 1px dashed #f56c6c;
  padding: 8px;
  background-color: #fef0f0;
  border-radius: 4px;
  margin: 5px 0;
  font-family: monospace;
  white-space: pre-wrap;
  word-break: break-all;
}
</style>
